import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Alert, Spinner, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import qaService from '../services/qaService';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORIES_BY_ID,
  DOCUMENT_CATEGORIES_BY_NAME,
  DOCUMENT_CATEGORY_ORDER
} from '../config/documentCategories';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const REQUIRED_DOCUMENTS = DOCUMENT_CATEGORY_ORDER.map((key) => {
  const category = DOCUMENT_CATEGORIES[key];
  if (!category) return null;
  return {
    key,
    name: category.name,
    mandatory: category.mandatory
  };
}).filter(Boolean);

const ComplianceReports = () => {
  const { currentUser, userProfile } = useAuth();
  const [complianceData, setComplianceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [complianceStats, setComplianceStats] = useState({
    totalLearners: 0,
    compliantLearners: 0,
    pendingDocuments: 0,
    overdueDocuments: 0,
    complianceRate: 0,
    documentStats: {},
    provinceStats: {},
    cohortStats: {},
    programStats: {}
  });
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadComplianceData();
    }
  }, [currentUser, userProfile, selectedProvince, selectedCohort, selectedProgram]);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      
      // Load users (learners)
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const allUsers = usersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          registrationDate: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now())
        }))
        .filter(isRealUser);
      
      // Load cohorts
      const cohortsCollection = collection(db, 'cohorts');
      const cohortsSnapshot = await getDocs(cohortsCollection);
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load enrollments for completion data
      const enrollmentsCollection = collection(db, 'enrollments');
      const enrollmentsSnapshot = await getDocs(enrollmentsCollection);
      const enrollments = enrollmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        completedAt: doc.data().completedAt?.toDate?.() || null
      }));

      const enrolledLearnerIds = new Set(enrollments.map(enrollment => enrollment.userId).filter(Boolean));
      const users = allUsers.filter(user => {
        const roleValue = String(user.role || '').toLowerCase();
        if (roleValue === 'learner' || roleValue === 'student') {
          return true;
        }
        return enrolledLearnerIds.has(user.id);
      });
      
      // Load modules for training hours
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modules = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load programs
      const programsCollection = collection(db, 'programs');
      const programsSnapshot = await getDocs(programsCollection);
      const programsData = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load documents from learner subcollections (via QA service)
      const documents = await qaService.getAllDocuments().catch(() => []);
      const documentsByUser = documents.reduce((acc, doc) => {
        if (!doc.userId) return acc;
        if (!acc[doc.userId]) {
          acc[doc.userId] = [];
        }
        acc[doc.userId].push(doc);
        return acc;
      }, {});

      // Process compliance data
      const processedData = processComplianceData(users, enrollments, modules, cohortsData, programsData, documentsByUser);
      
      setCohorts(cohortsData);
      setPrograms(programsData);
      setComplianceData(processedData.complianceRecords);
      setComplianceStats(processedData.stats);
      
    } catch (error) {
      devError('Error loading compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processComplianceData = (users, enrollments, modules, cohorts, programs, documentsByUser) => {
    const documentStats = {};
    const provinceStats = {};
    const cohortStats = {};
    const programStats = {};

    const toDate = (value) => {
      if (!value) return null;
      if (typeof value.toDate === 'function') return value.toDate();
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const resolveDocumentKey = (document) => {
      if (document.categoryKey && DOCUMENT_CATEGORIES[document.categoryKey]) {
        return document.categoryKey;
      }
      if (document.categoryId && DOCUMENT_CATEGORIES_BY_ID[document.categoryId]) {
        return DOCUMENT_CATEGORIES_BY_ID[document.categoryId].key;
      }
      if (document.category && DOCUMENT_CATEGORIES_BY_NAME[document.category]) {
        return DOCUMENT_CATEGORIES_BY_NAME[document.category].key;
      }
      return null;
    };

    // Initialize stats
    REQUIRED_DOCUMENTS.forEach(doc => {
      documentStats[doc.key] = {
        name: doc.name,
        mandatory: doc.mandatory,
        submitted: 0,
        missing: 0,
        complianceRate: 0
      };
    });

    const complianceRecords = users.map(user => {
      const userEnrollments = enrollments.filter(e => e.userId === user.id);
      const completedEnrollments = userEnrollments.filter(e => e.completed);

      const programId = user.programId || user.currentProgram || 'Unknown';
      const programName = programs.find(p => p.id === programId)?.name || 'Unknown';

      // Calculate training hours
      const totalTrainingHours = completedEnrollments.reduce((total, enrollment) => {
        const module = modules.find(m => m.id === enrollment.moduleId);
        return total + (module?.estimatedHours || 0);
      }, 0);

      // Check document compliance
      const userDocs = documentsByUser[user.id] || [];
      const documentCompliance = {};
      const documentsByKey = {};

      userDocs.forEach((doc) => {
        const key = resolveDocumentKey(doc);
        if (!key) return;
        if (!documentsByKey[key]) {
          documentsByKey[key] = doc;
        }
      });

      let mandatoryDocsSubmitted = 0;
      let totalMandatoryDocs = 0;

      REQUIRED_DOCUMENTS.forEach(doc => {
        const matchingDoc = documentsByKey[doc.key];
        const hasFile = matchingDoc?.fileURL || matchingDoc?.downloadURL || (Array.isArray(matchingDoc?.files) && matchingDoc.files.length > 0);
        const isSubmitted = Boolean(matchingDoc && hasFile);
        const uploadDate = toDate(matchingDoc?.uploadedAt || matchingDoc?.uploadDate);

        documentCompliance[doc.key] = {
          submitted: isSubmitted,
          uploadDate: isSubmitted ? uploadDate : null,
          status: isSubmitted ? 'submitted' : 'missing'
        };

        if (doc.mandatory) {
          totalMandatoryDocs++;
          if (isSubmitted) {
            mandatoryDocsSubmitted++;
            documentStats[doc.key].submitted++;
          } else {
            documentStats[doc.key].missing++;
          }
        } else if (isSubmitted) {
          documentStats[doc.key].submitted++;
        } else {
          documentStats[doc.key].missing++;
        }
      });

      // Determine compliance status
      const documentComplianceRate = totalMandatoryDocs > 0 ? (mandatoryDocsSubmitted / totalMandatoryDocs) * 100 : 0;
      const isFullyCompliant = documentComplianceRate === 100 && totalTrainingHours >= 8; // 8 hours minimum
      const hasPartialCompliance = documentComplianceRate >= 50 || totalTrainingHours >= 4;

      let complianceStatus = 'non_compliant';
      if (isFullyCompliant) complianceStatus = 'compliant';
      else if (hasPartialCompliance) complianceStatus = 'partial';

      // Update province stats
      const province = user.province || 'Unknown';
      if (!provinceStats[province]) {
        provinceStats[province] = {
          total: 0,
          compliant: 0,
          partial: 0,
          nonCompliant: 0
        };
      }
      provinceStats[province].total++;
      provinceStats[province][complianceStatus === 'compliant' ? 'compliant' :
                               complianceStatus === 'partial' ? 'partial' : 'nonCompliant']++;

      // Update cohort stats
      const cohortId = user.cohortId || 'Unknown';
      if (!cohortStats[cohortId]) {
        cohortStats[cohortId] = {
          name: cohorts.find(c => c.id === cohortId)?.name || 'Unknown',
          total: 0,
          compliant: 0,
          partial: 0,
          nonCompliant: 0
        };
      }
      cohortStats[cohortId].total++;
      cohortStats[cohortId][complianceStatus === 'compliant' ? 'compliant' : 
                            complianceStatus === 'partial' ? 'partial' : 'nonCompliant']++;

      // Update program stats
      if (!programStats[programId]) {
        programStats[programId] = {
          name: programName,
          total: 0,
          compliant: 0,
          partial: 0,
          nonCompliant: 0
        };
      }
      programStats[programId].total++;
      programStats[programId][complianceStatus === 'compliant' ? 'compliant' :
                              complianceStatus === 'partial' ? 'partial' : 'nonCompliant']++;

      return {
        ...user,
        programId,
        programName,
        documentCompliance,
        documentComplianceRate: Math.round(documentComplianceRate),
        totalTrainingHours,
        completedCourses: completedEnrollments.length,
        totalEnrollments: userEnrollments.length,
        complianceStatus,
        isFullyCompliant,
        lastActivityDate: userEnrollments.length > 0
          ? new Date(Math.max(...userEnrollments.map(e => e.lastAccessedAt?.toDate?.() || e.createdAt?.toDate?.() || new Date())))
          : null
      };
    });
    
    // Filter by province and cohort
    const filteredRecords = complianceRecords.filter(record => {
      const provinceMatch = selectedProvince === 'all' || record.province === selectedProvince;
      const cohortMatch = selectedCohort === 'all' || record.cohortId === selectedCohort;
      const programMatch = selectedProgram === 'all' || record.programId === selectedProgram;
      return provinceMatch && cohortMatch && programMatch;
    });
    
    // Calculate final document stats
    Object.keys(documentStats).forEach(docKey => {
      const stats = documentStats[docKey];
      const total = stats.submitted + stats.missing;
      stats.complianceRate = total > 0 ? Math.round((stats.submitted / total) * 100) : 0;
    });
    
    // Calculate overall stats
    const totalLearners = filteredRecords.length;
    const compliantLearners = filteredRecords.filter(r => r.isFullyCompliant).length;
    const pendingDocuments = filteredRecords.reduce((sum, record) => {
      return sum + REQUIRED_DOCUMENTS.filter(doc =>
        doc.mandatory && !record.documentCompliance[doc.key].submitted
      ).length;
    }, 0);
    
    const stats = {
      totalLearners,
      compliantLearners,
      pendingDocuments,
      overdueDocuments: filteredRecords.filter(r => {
        const daysSinceRegistration = (Date.now() - r.registrationDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceRegistration > 30 && !r.isFullyCompliant; // 30 days grace period
      }).length,
      complianceRate: totalLearners > 0 ? Math.round((compliantLearners / totalLearners) * 100) : 0,
      documentStats,
      provinceStats,
      cohortStats,
      programStats
    };
    
    return {
      complianceRecords: filteredRecords,
      stats
    };
  };

  const exportToCSV = () => {
    const headers = [
      'Name',
      'Email',
      'ID Number',
      'Province',
      'Program',
      'Cohort',
      'Registration Date',
      'Document Compliance Rate',
      'Training Hours',
      'Completed Courses',
      'Compliance Status',
      ...REQUIRED_DOCUMENTS.map(doc => doc.name),
      'Last Activity'
    ];
    
    const csvData = [headers.join(',')];
    
    complianceData.forEach(record => {
      const cohortName = cohorts.find(c => c.id === record.cohortId)?.name || 'N/A';
      const row = [
        record.displayName || '',
      record.email || '',
      record.idNumber || '',
      record.province || '',
      record.programName || '',
      cohortName,
        record.registrationDate ? record.registrationDate.toISOString().split('T')[0] : '',
        `${record.documentComplianceRate}%`,
        record.totalTrainingHours,
        record.completedCourses,
        record.complianceStatus,
        ...REQUIRED_DOCUMENTS.map(doc =>
          record.documentCompliance[doc.key]?.submitted ? 'Yes' : 'No'
        ),
        record.lastActivityDate ? record.lastActivityDate.toISOString().split('T')[0] : 'Never'
      ];
      csvData.push(row.join(','));
    });
    
    const csvContent = csvData.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Compliance_Report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getComplianceStatusBadge = (status) => {
    const variants = {
      'compliant': 'success',
      'partial': 'warning',
      'non_compliant': 'danger'
    };
    const labels = {
      'compliant': 'Compliant',
      'partial': 'Partial',
      'non_compliant': 'Non-Compliant'
    };
    return <Badge bg={variants[status]}>{labels[status]}</Badge>;
  };

  const getUniqueProvinces = () => {
    const provinces = [...new Set(complianceData.map(record => record.province).filter(Boolean))];
    return provinces.sort();
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access compliance reports.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="compliance-reports" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading compliance reports...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="compliance-reports" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Compliance Reports</h1>
              <p className="mission-text mb-4">
                Monitor regulatory compliance and document submission status
              </p>
              <p className="mb-0">
                Track learner document submissions, compliance rates, and regulatory
                requirements across provinces and cohorts with comprehensive reporting.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="outline-light" onClick={exportToCSV}>
                Export CSV
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {/* Overall Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{complianceStats.totalLearners}</h3>
                <small className="text-muted">Total Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{complianceStats.compliantLearners}</h3>
                <small className="text-muted">Fully Compliant</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{complianceStats.pendingDocuments}</h3>
                <small className="text-muted">Pending Documents</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-danger">{complianceStats.overdueDocuments}</h3>
                <small className="text-muted">Overdue Submissions</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={8}>
            <Card>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Filter by Province</Form.Label>
                      <Form.Select 
                        value={selectedProvince} 
                        onChange={(e) => setSelectedProvince(e.target.value)}
                      >
                        <option value="all">All Provinces</option>
                        {getUniqueProvinces().map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Filter by Program</Form.Label>
                      <Form.Select
                        value={selectedProgram}
                        onChange={(e) => setSelectedProgram(e.target.value)}
                      >
                        <option value="all">All Programs</option>
                        {programs.map(program => (
                          <option key={program.id} value={program.id}>{program.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Filter by Cohort</Form.Label>
                      <Form.Select 
                        value={selectedCohort} 
                        onChange={(e) => setSelectedCohort(e.target.value)}
                      >
                        <option value="all">All Cohorts</option>
                        {cohorts.map(cohort => (
                          <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Header>
                <h5>Overall Compliance Rate</h5>
              </Card.Header>
              <Card.Body className="text-center">
                <h2 className={`text-${complianceStats.complianceRate >= 80 ? 'success' : complianceStats.complianceRate >= 60 ? 'warning' : 'danger'}`}>
                  {complianceStats.complianceRate}%
                </h2>
                <ProgressBar 
                  now={complianceStats.complianceRate} 
                  variant={complianceStats.complianceRate >= 80 ? 'success' : complianceStats.complianceRate >= 60 ? 'warning' : 'danger'}
                  className="mt-2"
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="overview" className="mb-4">
          <Tab eventKey="overview" title="Compliance Overview">
            <Card>
              <Card.Header>
                <h5>Document Compliance Status</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Document Type</th>
                      <th>Mandatory</th>
                      <th>Submitted</th>
                      <th>Missing</th>
                      <th>Compliance Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(complianceStats.documentStats).map(([key, stats]) => (
                      <tr key={key}>
                        <td>
                          <strong>{stats.name}</strong>
                        </td>
                        <td>
                          {stats.mandatory ? (
                            <Badge bg="danger">Required</Badge>
                          ) : (
                            <Badge bg="info">Optional</Badge>
                          )}
                        </td>
                        <td>
                          <Badge bg="success">{stats.submitted}</Badge>
                        </td>
                        <td>
                          <Badge bg="warning">{stats.missing}</Badge>
                        </td>
                        <td>
                          <div className="mb-1">
                            <Badge bg={stats.complianceRate >= 80 ? 'success' : stats.complianceRate >= 60 ? 'warning' : 'danger'}>
                              {stats.complianceRate}%
                            </Badge>
                          </div>
                          <ProgressBar 
                            now={stats.complianceRate} 
                            variant={stats.complianceRate >= 80 ? 'success' : stats.complianceRate >= 60 ? 'warning' : 'danger'}
                            size="sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
          
          <Tab eventKey="learners" title="Learner Details">
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5>Individual Compliance Status</h5>
                  <Badge bg="info">{complianceData.length} learners</Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Learner</th>
                        <th>Program</th>
                        <th>Province</th>
                        <th>Document Progress</th>
                        <th>Training Hours</th>
                        <th>Status</th>
                        <th>Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {complianceData.map((record) => (
                        <tr key={record.id}>
                          <td>
                            <div>
                              <strong>{record.displayName}</strong>
                              <div><small className="text-muted">{record.email}</small></div>
                            </div>
                          </td>
                          <td>{record.programName || 'N/A'}</td>
                          <td>{record.province}</td>
                          <td>
                            <div className="mb-1">
                              <small>{record.documentComplianceRate}% complete</small>
                            </div>
                            <ProgressBar 
                              now={record.documentComplianceRate} 
                              variant={record.documentComplianceRate >= 80 ? 'success' : record.documentComplianceRate >= 50 ? 'warning' : 'danger'}
                              size="sm"
                            />
                          </td>
                          <td>
                            <Badge bg={record.totalTrainingHours >= 8 ? 'success' : 'warning'}>
                              {record.totalTrainingHours}h
                            </Badge>
                          </td>
                          <td>{getComplianceStatusBadge(record.complianceStatus)}</td>
                          <td>
                            {record.lastActivityDate ? (
                              <small>{record.lastActivityDate.toLocaleDateString()}</small>
                            ) : (
                              <small className="text-muted">No activity</small>
                            )}
                          </td>
                        </tr>
                      ))}
                      {complianceData.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">
                            No compliance records found for the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Tab>
          
          <Tab eventKey="statistics" title="Regional Stats">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Compliance by Province</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Province</th>
                          <th>Total</th>
                          <th>Compliant</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(complianceStats.provinceStats).map(([province, stats]) => {
                          const rate = stats.total > 0 ? Math.round((stats.compliant / stats.total) * 100) : 0;
                          return (
                            <tr key={province}>
                              <td><strong>{province}</strong></td>
                              <td><Badge bg="info">{stats.total}</Badge></td>
                              <td><Badge bg="success">{stats.compliant}</Badge></td>
                              <td>
                                <Badge bg={rate >= 80 ? 'success' : rate >= 60 ? 'warning' : 'danger'}>
                                  {rate}%
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Compliance by Cohort</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Cohort</th>
                          <th>Total</th>
                          <th>Compliant</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(complianceStats.cohortStats).map(([cohortId, stats]) => {
                          const rate = stats.total > 0 ? Math.round((stats.compliant / stats.total) * 100) : 0;
                          return (
                            <tr key={cohortId}>
                              <td><strong>{stats.name}</strong></td>
                              <td><Badge bg="info">{stats.total}</Badge></td>
                              <td><Badge bg="success">{stats.compliant}</Badge></td>
                              <td>
                                <Badge bg={rate >= 80 ? 'success' : rate >= 60 ? 'warning' : 'danger'}>
                                  {rate}%
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col md={12}>
                <Card>
                  <Card.Header>
                    <h5>Compliance by Program</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Program</th>
                          <th>Total</th>
                          <th>Compliant</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(complianceStats.programStats || {}).map(([programId, stats]) => {
                          const rate = stats.total > 0 ? Math.round((stats.compliant / stats.total) * 100) : 0;
                          return (
                            <tr key={programId}>
                              <td><strong>{stats.name}</strong></td>
                              <td><Badge bg="info">{stats.total}</Badge></td>
                              <td><Badge bg="success">{stats.compliant}</Badge></td>
                              <td>
                                <Badge bg={rate >= 80 ? 'success' : rate >= 60 ? 'warning' : 'danger'}>
                                  {rate}%
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Container>
    </>
  );
};

export default ComplianceReports;
